import { createApp } from 'vue';
import LfmSelect from './LfmSelect.vue';
import { DEFAULT_LANGUAGE, resolveTexts } from './i18n/index.js';

const mounted = new WeakMap();

function isSelectElement(el) {
    return el && el.tagName === 'SELECT';
}

function resolveElements(target) {
    if (!target) return [];
    if (typeof target === 'string') {
        return Array.from(document.querySelectorAll(target)).filter(isSelectElement);
    }
    if (target instanceof Element) {
        return isSelectElement(target) ? [target] : [];
    }
    if (typeof target[Symbol.iterator] === 'function') {
        return Array.from(target).filter(isSelectElement);
    }
    return [];
}

function readDataConfig(nativeSelect) {
    const { dataset } = nativeSelect;
    const config = {};
    if (dataset.placeholder !== undefined) {
        config.placeholder = dataset.placeholder;
    }
    if (dataset.searchable !== undefined) {
        config.searchable = dataset.searchable !== 'false' && dataset.searchable !== '0';
    }
    if (dataset.language !== undefined) {
        config.language = dataset.language;
    }
    return config;
}

export function enhanceSelects(target = 'select[data-lfm-select]', config = {}) {
    const elements = resolveElements(target);
    const handles = [];

    elements.forEach((nativeSelect) => {
        if (mounted.has(nativeSelect)) {
            handles.push(mounted.get(nativeSelect));
            return;
        }

        const mountEl = document.createElement('div');
        mountEl.className = 'lfm-select-mount';
        nativeSelect.insertAdjacentElement('afterend', mountEl);

        nativeSelect.dataset.lfmEnhanced = '';
        nativeSelect.hidden = true;

        const merged = { ...config, ...readDataConfig(nativeSelect) };
        const app = createApp(LfmSelect, {
            nativeSelect,
            searchable: merged.searchable,
            placeholder: merged.placeholder,
            texts: resolveTexts(merged.language ?? DEFAULT_LANGUAGE, config.texts),
        });
        app.mount(mountEl);

        const handle = {
            nativeSelect,
            mountEl,
            destroy() {
                app.unmount();
                mountEl.remove();
                nativeSelect.hidden = false;
                delete nativeSelect.dataset.lfmEnhanced;
                mounted.delete(nativeSelect);
            },
        };

        mounted.set(nativeSelect, handle);
        handles.push(handle);
    });

    return handles;
}

export function destroyEnhancedSelect(target) {
    resolveElements(target).forEach((el) => {
        const handle = mounted.get(el);
        if (handle) handle.destroy();
    });
}

export default enhanceSelects;
